import { IGenericInputProps, IGenericInputReturn } from "@/@types/GenericInputProps"
import AddonContent, { IAddonContentProps } from "@/app/components/atomic/Input/AddonContent"
import InputError from "@/app/components/atomic/Input/InputError"
import { InputMaskProps } from "@react-input/mask"
import dynamic from "next/dynamic"
import React, { ReactElement, useEffect, useState } from "react"
import { FormState, useFormContext } from "react-hook-form"

const NumericInput = dynamic(() => import("./NumericInput"), { ssr: false })

interface INumericInputProps extends IGenericInputProps {
    borders?: keyof typeof BORDERS
}

interface AppInputProps extends INumericInputProps {
    min?: number
    max?: number
    addonContent?: string | ReactElement
    prefixContent?: AppInputProps["addonContent"]
    addonTheme?: IAddonContentProps["theme"]
    required?: boolean
    debounce?: number | null
    placeholder?: string
    mask?: InputMaskProps["mask"]
    replacement?: InputMaskProps["replacement"]
    track?: InputMaskProps["track"]
    validation?: boolean
    disabled?: boolean
    thousandSeparator?: string | boolean
    decimalScale?: number
    decimalSeparator?: string
    allowedDecimalSeparators?: string[]
    fixedDecimalScale?: boolean
    autoComplete?: string
    patternFormat?: string
    onBlur?: () => void
}

const BORDERS = {
    default: "border border-blue-300 hover:border-blue-500 focus-within:border-blue-500",
    neutral: 'border border-rm-gray-light hover:border-rm-gray focus-within:border-rm-gray',
} as const

const DEFAULT_BORDERS = "default"

function AppNumericInput({
    value,
    onChange,
    onClick,
    fieldName,
    className,
    addonContent,
    prefixContent,
    addonTheme,
    required,
    min,
    max,
    debounce,
    borders = DEFAULT_BORDERS,
    disabled = false,
    placeholder,
    thousandSeparator = " ",
    decimalScale,
    decimalSeparator,
    allowedDecimalSeparators,
    fixedDecimalScale,
    autoComplete,
    patternFormat,
    onBlur,
}: AppInputProps) {
    const [error, setError] = useState(null)
    const bordersTheme = BORDERS[borders]
    const [isFocused, setIsFocused] = useState(false)
    const { formState = {}, setValue = () => {} }: { formState: FormState<any> | {}; setValue: Function } =
        useFormContext() || {}

    useEffect(() => {
        setValue(fieldName, value)
    }, [value, fieldName, setValue])

    const onChangeWithValidation = (inputReturn: IGenericInputReturn) => {
        setValue(fieldName, (inputReturn as any).value)
        onChange(inputReturn)
    }
    const renderInput = () => {
        return (
            <NumericInput
                value={value}
                onChange={onChangeWithValidation}
                onClick={onClick}
                fieldName={fieldName}
                className={className}
                required={required}
                min={min}
                max={max}
                debounce={debounce}
                disabled={disabled}
                placeholder={placeholder}
                thousandSeparator={thousandSeparator}
                decimalScale={decimalScale}
                decimalSeparator={decimalSeparator}
                allowedDecimalSeparators={allowedDecimalSeparators}
                fixedDecimalScale={fixedDecimalScale}
                autoComplete={autoComplete}
                patternFormat={patternFormat}
            />
        )
    }

    return (
        <div
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
                setIsFocused(false)
                onBlur?.()
            }}
            className={`flex relative items-center rounded-rm-10 w-full min-h-12 transition-all duration-500 ${
                error ? "border-red-500" : ""
            } ${
                disabled
                    ? "border !bg-gray-100 !border-gray-200 hover:border-gray-200 !text-gray-600"
                    : `${bordersTheme} bg-white`
            }`}
        >
            {prefixContent && <AddonContent theme={addonTheme}>{prefixContent}</AddonContent>}
            <InputError formState={formState} fieldName={fieldName} isFocused={isFocused} onError={setError}>
                {renderInput()}
            </InputError>
            {addonContent && (
                <AddonContent theme={addonTheme} place={"right"}>
                    {addonContent}
                </AddonContent>
            )}
        </div>
    )
}

export default React.memo(AppNumericInput)
